import React, { useState } from 'react'
import {
  Grid,
  useMediaQuery,
  Card,
  CardContent,
  Container,
  Box,
  Typography,
  useTheme,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Add, Close } from '@mui/icons-material'
import RichText from '../RichText'
import { TextColumn, TextColumnVariant } from '../../types/heroTextSection'
import Image from './Image'
import { BLOCKS } from '@contentful/rich-text-types'
import getColor, { getTextColor } from '../../utils/colors'
import useBrand from '../../hooks/useBrand'
import { BindToFields, CommonClasses } from '../../types/contentfulAttributes'
import formatContentfulClasses, {
  getModuleClasses,
} from '../../utils/customClasses'
import * as successStoryClasses from '../../../topresume.com/src/styles/success-stories.module.css'
import { createContentfulClassGetter } from '../../utils/contentful'

interface TextColumnProps {
  textColumnInsert: TextColumn
  containerClass?: string
  textClass?: string
}

export const TextColumnInsert: React.FC<TextColumnProps> = ({
  textColumnInsert,
  containerClass,
  textClass,
}) => {
  const { classes, cx } = useStyles(textColumnInsert)
  const brand = useBrand()

  const applyContentfulStyles = createContentfulClassGetter(
    textColumnInsert.css
  )
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [isExpanded, setIsExpanded] = useState(false) // Start collapsed on mobile

  const handleExpansion = () => {
    // Only toggle expansion on mobile
    if (isMobile) {
      setIsExpanded(!isExpanded)
    }
  }

  switch (textColumnInsert.variant) {
    case TextColumnVariant.Small:
    case TextColumnVariant.SmallHighlight:
      return (
        <Container
          className={cx(
            {
              [classes.container]: true,
              containerClass: true,
            },
            ...formatContentfulClasses(textColumnInsert.codeClasses),
            ...getModuleClasses(
              textColumnInsert.codeClasses,
              successStoryClasses
            ),
            applyContentfulStyles(BindToFields.Main)
          )}
          disableGutters
        >
          <Card
            elevation={0}
            className={cx(classes.card, {
              [classes.background]: !!textColumnInsert.backgroundColor,
              [classes.orColumn]: textColumnInsert.codeClasses?.includes(
                CommonClasses.OrColumn
              ),
            })}
          >
            <CardContent className={classes.cardContent}>
              {textColumnInsert.image && (
                <Image
                  className={classes.image}
                  asset={textColumnInsert.image}
                  objectFit="contain"
                />
              )}
              <Box className={cx(classes.cardContent, textClass)}>
                <RichText
                  cssEntries={textColumnInsert.css}
                  rawBody={textColumnInsert.text.raw}
                  references={textColumnInsert.text.references}
                  options={{
                    props: {
                      [BLOCKS.HEADING_1]: {
                        className: cx({
                          [classes.highlight]:
                            textColumnInsert.variant ===
                            TextColumnVariant.SmallHighlight,
                        }),
                      },
                      [BLOCKS.HEADING_2]: {
                        className: cx({
                          [classes.title]: true,
                          [classes.highlight]:
                            textColumnInsert.variant ===
                            TextColumnVariant.SmallHighlight,
                        }),
                      },
                      [BLOCKS.HEADING_3]: {
                        className: cx({
                          [classes.title]: true,
                          [classes.highlight]:
                            textColumnInsert.variant ===
                            TextColumnVariant.SmallHighlight,
                        }),
                      },
                      [BLOCKS.HEADING_4]: {
                        className: cx({
                          [classes.title]: true,
                          [classes.highlight]:
                            textColumnInsert.variant ===
                            TextColumnVariant.SmallHighlight,
                        }),
                      },
                      [BLOCKS.HR]: {
                        className: classes.hr,
                      },
                      [BLOCKS.PARAGRAPH]: {
                        className: classes.text,
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Container>
      )
    case TextColumnVariant.Logo:
      return (
        <Container
          className={cx(containerClass, {
            [classes.background]: !!textColumnInsert.backgroundColor,
            [classes.containerLogo]:
              textColumnInsert.variant === TextColumnVariant.Logo,
          })}
          disableGutters
        >
          <Image
            className={cx({
              [classes.logoImage]:
                textColumnInsert.variant === TextColumnVariant.Logo,
            })}
            asset={textColumnInsert.image}
          />
          <Box className={cx(classes.cardContent, textClass)}>
            <RichText
              cssEntries={textColumnInsert.css}
              rawBody={textColumnInsert.text.raw}
              references={textColumnInsert.text.references}
              options={{
                props: {
                  [BLOCKS.HR]: {
                    className: classes.hr,
                  },
                  [BLOCKS.PARAGRAPH]: {
                    className: classes.text,
                  },
                },
              }}
            />
          </Box>
        </Container>
      )
    case TextColumnVariant.Badge:
    case TextColumnVariant.Medium:
      return (
        <Box
          className={cx(
            containerClass,
            {
              [classes.background]: !!textColumnInsert.backgroundColor,
              [classes.containerMedium]:
                textColumnInsert.variant === TextColumnVariant.Medium,
              [classes.containerBadge]:
                textColumnInsert.variant === TextColumnVariant.Badge,
              [classes.textColumnWriter]:
                textColumnInsert.codeClasses?.includes(
                  CommonClasses.TextColumnWriter
                ),
            },
            ...formatContentfulClasses(textColumnInsert.codeClasses),
            ...getModuleClasses(
              textColumnInsert.codeClasses,
              successStoryClasses
            )
          )}
        >
          <Box
            className={cx({
              [classes.cardContentBadge]: true,
              // @ts-ignore
              [textClass]: true,
              [classes.cardContentBadgeTR]: brand.name !== 'ZipJob',
            })}
          >
            {textColumnInsert.image && (
              <Image
                className={cx({
                  [classes.badgeImage]:
                    textColumnInsert.variant === TextColumnVariant.Medium,
                })}
                asset={textColumnInsert.image}
              />
            )}
            <RichText
              cssEntries={textColumnInsert.css}
              rawBody={textColumnInsert.text.raw}
              references={textColumnInsert.text.references}
              options={{
                props: {
                  [BLOCKS.HR]: {
                    className: classes.hr,
                  },
                  [BLOCKS.PARAGRAPH]: {
                    className: cx({
                      [classes.textBadge]: true,
                      [classes.textBadgeTR]: brand.name !== 'ZipJob',
                      [classes.textColumnWriterParagraph]:
                        textColumnInsert.codeClasses?.includes(
                          CommonClasses.TextColumnWriter
                        ),
                    }),
                  },
                },
              }}
            />
          </Box>
        </Box>
      )
    case TextColumnVariant.Hover:
      return (
        <Grid item xs={12} container sm={6} md={4}>
          <Container
            className={cx(classes.noWrapGroupContainer)}
            disableGutters
          >
            <Card
              elevation={0}
              className={cx(classes.cardHover, classes.background)}
              onClick={isMobile ? handleExpansion : undefined} // Only allow expansion on mobile
            >
              <CardContent className={classes.cardContentHover}>
                {textColumnInsert.backgroundimage && (
                  <Image
                    asset={textColumnInsert.backgroundimage}
                    className={classes.imageHoverBackground}
                  />
                )}
                {(!isMobile || (isMobile && isExpanded)) &&
                  textColumnInsert.image && (
                    <Image
                      className={cx(classes.imageHover)}
                      asset={textColumnInsert.image}
                    />
                  )}
                {isMobile && (
                  <Typography
                    className={classes.hoverTitleSecondMobile}
                    style={{ order: isExpanded ? -1 : 1 }}
                  >
                    {textColumnInsert.image && (
                      <Image
                        className={cx(classes.imageHoverMobile)}
                        asset={textColumnInsert.image}
                      />
                    )}
                    <strong>{textColumnInsert.name}</strong>
                    <div className={classes.plusIcon}>
                      {isExpanded ? <Close /> : <Add />}
                    </div>
                  </Typography>
                )}
                <Typography
                  className={classes.hoverTitleFirst}
                  style={{
                    display:
                      !isMobile || (isMobile && isExpanded) ? 'block' : 'none',
                  }}
                >
                  <strong>{textColumnInsert.name}</strong>
                </Typography>
                <Box
                  className={cx(classes.textBoxHover, textClass)}
                  style={{
                    display:
                      !isMobile || (isMobile && isExpanded) ? 'block' : 'none',
                  }}
                >
                  <RichText
                    cssEntries={textColumnInsert.css}
                    rawBody={textColumnInsert.text.raw}
                    references={textColumnInsert.text.references}
                    options={{
                      props: {
                        [BLOCKS.HR]: {
                          className: classes.hr,
                        },
                        [BLOCKS.PARAGRAPH]: {
                          className: classes.cardContentHoverPara,
                        },
                      },
                    }}
                  />
                </Box>
                <Typography className={classes.hoverTitleSecond}>
                  <strong>{textColumnInsert.name}</strong>
                  <div className={classes.plusIcon}>
                    <Add />
                  </div>
                </Typography>
              </CardContent>
            </Card>
          </Container>
        </Grid>
      )
    case TextColumnVariant.Large:
      return (
        <Container className={cx(containerClass)}>
          <Card
            elevation={0}
            className={cx(classes.cardLarge, classes.background)}
          >
            <CardContent className={classes.cardContentLarge}>
              {textColumnInsert.image && (
                <Image
                  className={classes.imageLarge}
                  asset={textColumnInsert.image}
                />
              )}
              <Box className={cx(classes.textBox, textClass)}>
                <RichText
                  cssEntries={textColumnInsert.css}
                  rawBody={textColumnInsert.text.raw}
                  references={textColumnInsert.text.references}
                  options={{
                    props: {
                      [BLOCKS.HR]: {
                        className: classes.hr,
                      },
                      [BLOCKS.PARAGRAPH]: {
                        className: classes.cardContentLargePara,
                      },
                      [BLOCKS.HEADING_1]: {
                        className: applyContentfulStyles(
                          BindToFields.RichTextH1
                        ),
                      },
                    },
                  }}
                />
              </Box>
              <Typography variant={'caption'}>
                {textColumnInsert.name}
              </Typography>
            </CardContent>
          </Card>
        </Container>
      )
    case TextColumnVariant.Flipped:
      return (
        <Container
          className={cx(
            classes.containerFlipped,
            classes.background,
            containerClass
          )}
          disableGutters
        >
          <Box className={cx(textClass)}>
            <RichText
              cssEntries={textColumnInsert.css}
              rawBody={textColumnInsert.text.raw}
              references={textColumnInsert.text.references}
            />
          </Box>
          {textColumnInsert.image && (
            <Image asset={textColumnInsert.image} objectFit="contain" />
          )}
        </Container>
      )
    default:
      return null
  }
}

const useStyles = makeStyles<
  TextColumn,
  'imageHover' | 'textBoxHover' | 'hoverTitleFirst' | 'hoverTitleSecond'
>()((theme, textColumn, classes) => {
  const bgColor = getColor(textColumn?.backgroundColor, theme)
  const textColor = getTextColor(textColumn?.backgroundColor, theme)

  const cardContentAlignment =
    textColumn.alignment === 'left' ? 'flex-start' : 'center'

  const textAlignment = textColumn.alignment

  return {
    background: {
      backgroundColor: bgColor,
      color: textColor,
    },
    container: {
      display: 'flex',
      flexDirection: 'row' as any,
      justifyContent: 'space-evenly',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        justifyContent: 'space-around',
      },
      alignItems: 'center' as any,
      margin: '0',
      paddingBottom: 0,
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column' as any,
        margin: '1em 0',
        maxWidth: 'unset',
        width: '100%',
      },
    },
    containerMedium: {
      display: 'flex',
      flexDirection: 'column' as any,
      flexWrap: 'wrap',
      textAlign: 'center',
      alignItems: 'center' as any,
      [theme.breakpoints.down('md')]: {
        width: '100%',

        '&:last-child > div': {
          marginBottom: 0,
        },
      },
    },
    containerBadge: {
      width: '90%',
      display: 'flex',
      flexDirection: 'column' as any,
      textAlign: 'center',
      marginLeft: 0,
      [theme.breakpoints.down('md')]: {
        width: '100%',
        alignItems: 'center' as any,
      },
    },
    containerLogo: {
      width: '18rem',
      display: 'flex',
      flexDirection: 'column' as any,
      alignItems: 'center' as any,
      textAlign: 'center',
      marginTop: '3rem',
    },
    containerFlipped: {
      alignItems: 'center' as any,
      display: 'flex',
      flexDirection: 'column' as any,
      textAlign: 'center',
    },
    card: {
      backgroundColor: 'transparent',
      margin: 0,
      padding: 0,
      width: '100%',
    },
    cardLarge: {
      padding: '1.5rem 2.5rem',
      marginTop: '1.4375rem',
      border: `1px solid ${theme.palette.divider}`,
      [theme.breakpoints.down('md')]: {
        padding: '1.5rem',
      },
    },
    cardContent: {
      display: 'flex',
      flexDirection: 'column' as any,
      justifyContent: 'center',
      alignItems: cardContentAlignment,
      textAlign: textAlignment,
      paddingBottom: '0 !important',
      width: '100%',

      [theme.breakpoints.down('md')]: {
        paddingBottom: '0 !important',
      },
      '& *': {
        maxWidth: '100%',
      },
    },
    cardContentBadge: {
      display: 'flex',
      maxWidth: '21.3125rem',
      flexDirection: 'column' as any,
      justifyContent: 'space-between',
      flexBasis: '100%',
      alignItems: cardContentAlignment,
      textAlign: textAlignment,
      paddingBottom: '0 !important',
      [theme.breakpoints.down('md')]: {
        paddingBottom: '0 !important',
      },
      '&  *': {
        maxWidth: '100%',
      },
    },
    cardContentBadgeTR: {
      margin: '1.09375rem',

      '& *': {
        maxWidth: '100%',
      },

      [theme.breakpoints.down('md')]: {
        maxWidth: '100%',
        marginInline: 0,
      },
    },
    cardContentLarge: {
      display: 'flex',
      flexDirection: 'column' as any,
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      marginTop: '.6875rem',
    },
    cardContentLargePara: {
      fontSize: '1.5em',
      lineHeight: '1.45',
      [theme.breakpoints.down('md')]: {
        fontSize: '1.125rem',
        lineHeight: 1.55,
      },
    },
    hr: {
      backgroundColor: theme.palette.divider,
      height: '1px',
      margin: '0',
      width: '100%',
    },
    image: {
      width: '3.6rem',
    },
    logoImage: {
      marginBottom: '1rem',
      '&& img': {
        height: '2rem',
        width: 'auto',
        objectFit: 'contain',
      },
    },
    badgeImage: {
      width: '18.75rem',
      marginBottom: '.5rem',

      '& img': {
        objectFit: 'contain !important' as 'contain',
      },

      [theme.breakpoints.down('md')]: {
        width: '100%',
      },
    },
    imageLarge: {
      width: '15.5rem',
      [theme.breakpoints.down('md')]: {
        width: '10.5rem',
      },
    },
    title: {
      marginTop: '.5rem',
      [theme.breakpoints.down('md')]: {
        lineHeight: '1',
      },
    },
    highlight: {
      color: theme.palette.primary.main,
    },
    text: {
      margin: 0,
      [theme.breakpoints.down('md')]: {
        fontSize: '1rem',
      },
      '& > a': {
        color: theme.palette.text.disabled,
      },
      '& > a:hover': {
        color: 'white',
      },
    },
    textBadge: {
      width: '90%',
      margin: '1rem',
      '&:last-child': {
        marginBottom: '0',
      },
    },
    textBadgeTR: {
      margin: '.5rem 1rem',
    },
    imgTitle: {
      fontSize: '1rem',
    },
    textBox: {
      maxWidth: '100%',
    },
    orColumn: {
      width: '3rem',
      height: '3rem',
      borderRadius: '50%',

      '& div': {
        padding: 0,
        height: '3rem',
      },
    },
    textColumnWriterParagraph: {
      textAlign: 'left',
    },
    textColumnWriter: {
      '& > div > h5:nth-of-type(2) ,  & > div > h5:nth-of-type(3)': {
        display: 'flex',
        gap: '0.9rem',
        alignSelf: 'flex-start',
        textAlign: 'left',
      },
      '& > div > h5:nth-of-type(2)': {
        marginBottom: '0.75em',
      },
    },
    cardHover: {
      maxHeight: '230px',
      cursor: 'pointer',
      // cursor: 'default',
      padding: '0rem 0rem',
      marginTop: '1rem',
      borderRadius: '24px',
      marginBottom: '0rem',
      [theme.breakpoints.up('sm')]: {
        minHeight: '225px',
        marginBottom: '1rem',
      },

      [`&:hover .${classes.imageHover}`]: {
        transition: 'opacity 0.3s ease-in-out',
        opacity: 1,
        marginTop: '16px',
      },
      [`&:hover .${classes.textBoxHover}`]: {
        transition: 'opacity 0.3s ease-in-out',
        opacity: 1,
        zIndex: 9,
      },
      [`&:hover .${classes.hoverTitleFirst}`]: {
        transition: 'opacity 0.3s ease-in-out',
        opacity: 1,
        display: 'block',
      },
      [`&:hover .${classes.hoverTitleSecond}`]: {
        display: 'block',
        transition: 'opacity 0s ease-in-out',
        opacity: 0,
      },
      [theme.breakpoints.down('sm')]: {
        padding: '0rem',
        marginBottom: '1rem',
        [`&:hover .${classes.hoverTitleSecond}`]: {
          display: 'none!important',
        },
        [`&:hover .${classes.imageHover}`]: {
          display: 'none!important',
        },
        [`&:hover .${classes.textBoxHover}`]: {
          transition: 'opacity 0.3s ease-in-out',
          opacity: 1,
          zIndex: 9,
        },
        [`&:hover .${classes.hoverTitleFirst}`]: {
          opacity: 0,
          display: 'none!important',
        },
      },
    },
    cardContentHover: {
      padding: '8px 24px 0px 24px',
      minHeight: '210px',
      backgroundColor: '#F6F7FA',
      display: 'flex',
      flexDirection: 'column' as any,
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      textAlign: 'center',
      position: 'relative',
      [theme.breakpoints.up('sm')]: {
        minHeight: '225px',
      },
      [theme.breakpoints.down('sm')]: {
        minHeight: '25px',
        padding: '12px 14px 14px 12px!important',
      },
    },
    cardContentHoverPara: {
      fontSize: '1rem',
      lineHeight: '1.45',
      textAlign: 'left',
      [theme.breakpoints.down('md')]: {
        fontSize: '.8rem',
        lineHeight: 1.55,
      },
    },
    imageHover: {
      width: '35px',
      float: 'left',
      marginLeft: '4px',
      height: '55px',
      paddingBottom: '8px',
      opacity: 0,
      [theme.breakpoints.down('sm')]: {
        display: 'none!important',
      },
    },
    imageHoverMobile: {
      width: '20px',
      height: 'auto',
      float: 'left',
      display: 'block',
      position: 'relative',
      top: '2px',
      marginRight: '4px',
      opacity: 1,
    },
    textBoxHover: {
      width: '100%',
      opacity: 0,
      minHeight: '80px',
      [theme.breakpoints.up('sm')]: {
        minHeight: '125px',
      },
      [theme.breakpoints.down('sm')]: {
        opacity: 1,
        zIndex: 9,
      },
    },
    noWrapGroupContainer: {
      width: '90%',
      maxWidth: '100%',
      [theme.breakpoints.down('lg')]: {
        width: '95%',
      },
      [theme.breakpoints.down('md')]: {
        width: '90%',
      },
    },
    hoverTitleFirst: {
      alignItems: 'flex-start',
      fontSize: '24px!important',
      fontWeight: 700,
      position: 'relative',
      width: '100%',
      textAlign: 'left',
      opacity: 0,
      display: 'none',
      [theme.breakpoints.down('lg')]: {
        fontSize: '24px!important',
      },
      [theme.breakpoints.down('sm')]: {
        opacity: 0,
        display: 'none!important',
      },
    },
    hoverTitleSecond: {
      alignItems: 'flex-start',
      fontSize: '24px!important',
      position: 'relative',
      width: '100%',
      textAlign: 'left',
      display: 'block',
      bottom: '50px',
      [theme.breakpoints.down('sm')]: {
        display: 'none!important',
      },
    },

    hoverTitleSecondMobile: {
      alignItems: 'flex-start',
      fontSize: '1.2rem',
      position: 'relative',
      width: '100%',
      textAlign: 'left',
      display: 'block',
      bottom: '30px',
      zIndex: 2,
      [theme.breakpoints.down('sm')]: {
        bottom: '0px!important',
        fontSize: '1rem!important',
      },
    },
    plusIcon: {
      float: 'right',
      maxHeight: '1.5rem',
      paddingTop: '.3rem',
      [theme.breakpoints.down('sm')]: {
        top: '-4px',
        position: 'relative',
      },
      '& svg': {
        maxHeight: '1.5rem',
      },
    },
    imageHoverBackground: {
      position: 'absolute!important' as 'absolute',
      height: 'auto',
      opacity: '70%',
      width: 'auto',
      top: '-20%',
      [theme.breakpoints.down('sm')]: {
        top: '-100px!important',
        position: 'absolute!important' as 'absolute',
      },
    },
  }
})

export default TextColumnInsert
